/**
 * Form rules for the account screens. Each returns user-facing messages; an
 * empty list means valid. The server re-validates everything.
 */

import { isValidBackupCode } from './security';

export interface PasswordStrength {
  tooShort: boolean;
  needsNumber: boolean;
  needsLetter: boolean;
  needsSpecialChar: boolean;
  needsCap: boolean;
}

/**
 * The login page's change-password strength rule (`assets/js/main.js`
 * `checkPassStrength`), now shared: 10+ characters with a digit, a lowercase
 * and an uppercase letter, and a non-word character.
 */
export function passwordStrength(value: string | null | undefined): PasswordStrength {
  if (typeof value !== 'string') {
    return { tooShort: true, needsNumber: true, needsLetter: true, needsSpecialChar: true, needsCap: true };
  }
  return {
    tooShort: value.length < 10,
    needsNumber: !/[0-9]/.test(value),
    needsLetter: !/[a-z]/.test(value),
    needsSpecialChar: !/\W+/.test(value),
    needsCap: !/[A-Z]/.test(value),
  };
}

export function isPasswordStrong(value: string | null | undefined): boolean {
  const strength = passwordStrength(value);
  return !(strength.tooShort || strength.needsNumber || strength.needsLetter || strength.needsSpecialChar || strength.needsCap);
}

export function passwordStrengthMessages(value: string | null | undefined): string[] {
  const s = passwordStrength(value);
  const out: string[] = [];
  if (s.tooShort) out.push('At least 10 characters');
  if (s.needsCap) out.push('An uppercase letter');
  if (s.needsLetter) out.push('A lowercase letter');
  if (s.needsNumber) out.push('A number');
  if (s.needsSpecialChar) out.push('A symbol');
  return out;
}

export interface PasswordChangeInput {
  current: string;
  next: string;
  confirm: string;
}

export function validatePasswordChange(input: PasswordChangeInput): string[] {
  const errors: string[] = [];
  if (!input.current) errors.push('Enter your current password.');
  if (!input.next) {
    errors.push('Enter a new password.');
    return errors;
  }
  if (!isPasswordStrong(input.next)) errors.push('The new password needs: ' + passwordStrengthMessages(input.next).join(', ').toLowerCase() + '.');
  if (input.next !== input.confirm) errors.push('The new passwords do not match.');
  if (input.current && input.next === input.current) errors.push('Choose a password different from your current one.');
  return errors;
}

/** Digits only: TOTP is 6, SMS/voice codes 4-8 depending on the provider. */
export function normalizeCode(code: string): string {
  return (code || '').replace(/[\s-]/g, '');
}

export function isValidCode(code: string, kind: 'totp' | 'sent' = 'totp'): boolean {
  const clean = normalizeCode(code);
  return kind === 'totp' ? /^\d{6}$/.test(clean) : /^\d{4,8}$/.test(clean);
}

/** The code a method's input expects; a backup code is ten base32 characters. */
export function isValidMethodCode(type: string, code: string): boolean {
  if (type === 'backup_code') return isValidBackupCode(code);
  return isValidCode(code, type === 'totp' ? 'totp' : 'sent');
}

/** US numbers as the rest of Dealr stores them: 10 digits, a leading 1 dropped. */
export function normalizePhone(phone: string): string {
  let digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
  return digits;
}

export function isValidPhone(phone: string): boolean {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(normalizePhone(phone));
}

const MAX_EMAIL_LENGTH = 254;

/** local@domain.tld: one '@', no whitespace, a '.' inside the domain. Linear, no regex backtracking. */
export function isValidEmail(email: string): boolean {
  const value = (email || '').trim();
  if (value.length === 0 || value.length > MAX_EMAIL_LENGTH) return false;
  let at = -1;
  for (let i = 0; i < value.length; i++) {
    const c = value.charAt(i);
    if (c.trim() === '') return false;
    if (c === '@') {
      if (at !== -1) return false;
      at = i;
    }
  }
  if (at <= 0) return false;
  // Some '.' with at least one character on each side of it inside the domain.
  for (let i = at + 2; i < value.length - 1; i++) {
    if (value.charAt(i) === '.') return true;
  }
  return false;
}

export function validateContactValue(field: 'email' | 'phone', value: string): string | null {
  if (field === 'email') return isValidEmail(value) ? null : 'Enter a valid email address.';
  return isValidPhone(value) ? null : 'Enter a valid 10-digit phone number.';
}

export function contactPayloadValue(field: 'email' | 'phone', value: string): string {
  return field === 'email' ? value.trim() : normalizePhone(value);
}
